import { AgentChat as Chat } from '../../../home/components/agent-chat';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { v4 as uuid } from '@lukeed/uuid';

import { cn } from '@shadcn/ui/lib/utils';

import { useAgent } from '../../../home/hooks/use-agents';
import { useMemory, useMessages, useThreads } from '../../../home/hooks/use-memory';
import { useAuth } from '../../../../providers/authentication-provider';
import { AndsendModel } from '@andsend/services-agent-shared';
import { useSidebars } from '@shadcn/ui/hooks/use-sidebars';
import dayjs from 'dayjs';
import { MarkdownText } from '../../../home/components/assistant-ui/markdown-text';
import { ToolFallback } from '../../../home/components/assistant-ui/tool-fallback';
import { ArrowRightIcon, ChevronLeftIcon, Search, Users } from 'lucide-react';
import { MastraRuntimeProvider } from '../../../home/providers/mastra-runtime-provider';
import Thread from '../../../home/components/assistant-ui/thread';
import { SparklesIcon } from 'lucide-react';
import { AllToolsUI } from '../../../home/components/tools/all-tools-ui';
import { ActionChatHeader } from './action-chat-header';
import { Button } from '@shadcn/ui/components/ui/button';
import { DisplayActionsToolUI } from './tools/display-actions-tool-ui';

export const HAS_VISITED_AGENT_PAGE_KEY = 'hasVisitedAgentPage';
export const ActionChat = () => {
    const [searchParams] = useSearchParams();
    const threadId = searchParams.get('threadId') || undefined;
    const [agentId, setAgentId] = useState<string>('actionFeedAgent');
    const { memory } = useMemory(agentId);
    const [hasWelcomeRoot, setHasWelcomeRoot] = useState(false);

    // Monitor for the presence of aui-thread-welcome-root
    useEffect(() => {
        const checkWelcomeRoot = () => {
            const welcomeElement = document.querySelector('.aui-thread-welcome-root');
            setHasWelcomeRoot(!!welcomeElement);
        };

        // Initial check
        checkWelcomeRoot();

        // Create observer to watch for DOM changes
        const observer = new MutationObserver(checkWelcomeRoot);
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class'],
        });

        return () => observer.disconnect();
    }, []);

    return (
        <div
            className={cn(
                'flex flex-row w-full h-full [&_.aui-avatar-root]:w-12 [&_.aui-avatar-root]:h-12 [&_.aui-avatar-root>img]:scale-[0.70] [&_.aui-avatar-root>img]:object-contain [&_.aui-avatar-root]:bg-background-tertiary'
            )}
        >
            <div className="relative flex flex-col w-full h-full justify-center">
                <Link
                    to={{
                        pathname: '/new/actions-v2',
                        search: threadId ? '?threadId=' + threadId : '',
                    }}
                    className={cn('absolute top-3 left-4 z-10', {
                        hidden: !hasWelcomeRoot,
                    })}
                >
                    <Button variant="ghost" size="icon">
                        <ChevronLeftIcon className="w-4 h-4" />
                    </Button>
                </Link>
                <ActionChatHeader
                    className={cn({
                        hidden: hasWelcomeRoot,
                    })}
                />
                <Thread
                    memory={memory?.result}
                    welcome={{
                        message: 'Hi there 👋',
                        suggestions: [
                            {
                                prompt: 'Show me actions due in the next 48 hours that need immediate attention',
                                text: (
                                    <div className="bg-background-tertiary rounded-full p-2 px-4 text-sm font-medium">
                                        <span>Show urgent actions</span>
                                    </div>
                                ),
                            },
                            {
                                prompt: 'Prioritize my actions based on relationship value and engagement score',
                                text: (
                                    <div className="bg-background-tertiary rounded-full p-2 px-4 text-sm font-medium">
                                        <span>Prioritize by value and engagement</span>
                                    </div>
                                ),
                            },
                            {
                                prompt: 'Show me follow-up actions that are approaching their due dates this week',
                                text: (
                                    <div className="bg-background-tertiary rounded-full p-2 px-4 text-sm font-medium">
                                        <span>Due follow-ups</span>
                                    </div>
                                ),
                            },
                            /* {
                                prompt: 'Show me actions due in the next 48 hours that need immediate attention',
                                text: (
                                    <div className="bg-background-tertiary rounded-full p-2 px-4 text-sm font-medium">
                                        <span>Show urgent actions</span>
                                    </div>
                                ),
                            },
                            {
                                prompt: 'Prioritize my actions based on relationship value and engagement score',
                                text: (
                                    <div className="bg-background-tertiary rounded-full p-2 px-4 text-sm font-medium">
                                        <span>Prioritize by value and engagement</span>
                                    </div>
                                ),
                            },
                            {
                                prompt: 'Show me follow-up actions that are approaching their due dates this week',
                                text: (
                                    <div className="bg-background-tertiary rounded-full p-2 px-4 text-sm font-medium">
                                        <span>Due follow-ups</span>
                                    </div>
                                ),
                            },
                            /* {
                                prompt: 'Show me actions due in the next 48 hours that need immediate attention',
                                text: (
                                    <SuggestionRow
                                        icon={
                                            <SparklesIcon className="w-8 h-8 bg-violet-100 dark:bg-violet-900 rounded-full p-[9px] text-violet-600 dark:text-violet-300" />
                                        }
                                        title="Show urgent actions due in the next 48 hours"
                                        hoverGradient="from-violet-50 to-fuchsia-50 dark:from-violet-900 dark:to-fuchsia-900"
                                        arrowHoverColor="group-hover:text-violet-500 dark:group-hover:text-violet-300"
                                    />
                                ),
                            },
                            {
                                prompt: 'Prioritize my actions based on relationship value and engagement score',
                                text: (
                                    <SuggestionRow
                                        icon={
                                            <Users className="w-8 h-8 bg-rose-100 dark:bg-rose-900 rounded-full p-[9px] text-rose-600 dark:text-rose-300" />
                                        }
                                        title="Sort actions by relationship value and engagement"
                                        hoverGradient="from-rose-50 to-orange-50 dark:from-rose-900 dark:to-orange-900"
                                        arrowHoverColor="group-hover:text-rose-500 dark:group-hover:text-rose-300"
                                    />
                                ),
                            },
                            {
                                prompt: 'Show me follow-up actions that are approaching their due dates this week',
                                text: (
                                    <SuggestionRow
                                        icon={
                                            <Search className="w-8 h-8 bg-sky-100 dark:bg-sky-900 rounded-full p-[9px] text-sky-600 dark:text-sky-300" />
                                        }
                                        title="View follow-up actions due this week"
                                        hoverGradient="from-cyan-50 to-sky-50 dark:from-cyan-900 dark:to-sky-900"
                                        arrowHoverColor="group-hover:text-sky-500 dark:group-hover:text-sky-300"
                                    />
                                ),
                            }, */
                        ],
                    }}
                    assistantMessage={{
                        components: {
                            ToolFallback: ToolFallback,
                            Text: MarkdownText,
                        },
                    }}
                    assistantAvatar={{
                        src: '/images/logos/andsend-icon-striped-color.png',
                        fallback: 'A',
                    }}
                    composer={{
                        allowAttachments: false,
                    }}
                />
                <DisplayActionsToolUI />
            </div>
        </div>
    );
};

const SuggestionRow = ({
    title,
    icon,
    className,
    hoverGradient,
    arrowHoverColor,
}: {
    title: string;
    icon: React.ReactNode;
    className?: string;
    hoverGradient?: string; // e.g. 'from-violet-50 to-fuchsia-50'
    arrowHoverColor?: string; // e.g. 'group-hover:text-violet-500'
}) => {
    return (
        <div
            className={cn(
                'group relative overflow-hidden rounded-2xl border border-border-secondary hover:border-transparent hover:shadow-xs transition-all duration-300 w-full',
                className
            )}
        >
            {/* Gradient overlay for hover */}
            <div
                className={cn(
                    'absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none',
                    hoverGradient
                        ? `bg-linear-to-r ${hoverGradient}`
                        : 'bg-linear-to-r from-purple-50 to-purple-100 dark:from-purple-900 dark:to-purple-800'
                )}
            />
            <button
                type="button"
                className="w-full h-auto flex items-center justify-between py-4 px-4 rounded-xl text-gray-700 dark:text-gray-200 font-normal text-sm bg-transparent hover:bg-transparent relative z-10"
            >
                <div className="flex items-center gap-3">
                    {icon}
                    <span className="text-left">{title}</span>
                </div>
                <ArrowRightIcon
                    className={cn(
                        'w-4 h-4 text-gray-400 dark:text-gray-300 transition-colors duration-300',
                        arrowHoverColor
                    )}
                />
            </button>
        </div>
    );
};

export default ActionChat;
